import {
  GetStaticPaths,
  GetStaticPropsContext,
  InferGetStaticPropsType,
  NextPage,
} from 'next';

import { getCurrentISOString } from 'utils/date.utils';
import { getDefaultLocale } from 'utils/i18n.utils';
import { generateStaticPaths, replacePathParams } from 'utils/routes.utils';

import { Duration } from 'constants/duration.constants';
import { NOT_FOUND } from 'constants/nextjs.constants';
import { Routes } from 'constants/routes.constants';
import { cityEventsQuery, pageCityQuery } from 'queries/cities/queries';
import { datoClient } from 'services/api';
import Prefetcher from 'services/prefetcher';
import { AllPageCitySlugsDocument } from 'types/api/dato-operations.types';

import CityDetailContainer from 'components/@cities/CityDetailContainer';
import AppNavigation from 'components/@navigation/AppNavigation';
import PageSeo from 'components/@seo/PageSeo';

const CityDetail: NextPage<InferGetStaticPropsType<typeof getStaticProps>> = ({
  city,
}) => {
  return (
    <AppNavigation>
      <PageSeo tags={city.seoTags} />
      <CityDetailContainer key={city.id} city={city} />
    </AppNavigation>
  );
};

export default CityDetail;

export const getStaticPaths: GetStaticPaths = async ({
  locales,
  defaultLocale,
}) => {
  const locale = getDefaultLocale({ defaultLocale });
  const { allPageCities } = await datoClient().request(
    AllPageCitySlugsDocument,
    { locale },
  );

  const paths = generateStaticPaths(allPageCities, locales, ({ slug }) =>
    replacePathParams(Routes.CityDetail, { slug }),
  );

  return { paths, fallback: 'blocking' };
};

export const getStaticProps = async ({
  locale,
  params,
  preview,
}: GetStaticPropsContext<{ slug: string }>) => {
  const prefetcher = await Prefetcher.init({ locale, preview });

  const slug = params?.slug;

  if (!slug) return { ...NOT_FOUND };

  const [{ pageCity }] = await Promise.all([
    prefetcher.prefetch(pageCityQuery, {
      slug,
    }),
    prefetcher.prefetchInfinite(
      cityEventsQuery,
      {
        slug,
        currentISOString: getCurrentISOString(),
        orderBy: 'startDatetime_ASC',
      },
      { meta: { dehydrate: true } },
    ),
  ]);

  if (!pageCity) return { ...NOT_FOUND };

  return {
    props: {
      city: pageCity,
      dehydratedState: prefetcher.dehydratedState,
    },
    revalidate: Duration.TEN_MIN,
  };
};
